import {
	Dialog,
	DialogTitle,
	Stack,
	Button,
	TextField,
	Typography,
	Select,
	MenuItem,
	Box,
	DialogActions,
	DialogContent,
	IconButton,
} from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getGpsrPlans } from 'redux/reducers/gpsr/selectors';
import { changeGpsrServiceRequest, getGpsrPlansRequest } from 'redux/reducers/gpsr/reducer';
import { AnswersState, EParamType, EStepType, NextStep, Step, YesNoStep } from './types';
import { stepsConfig } from './steps';

const isNextStep = (s: Step): s is NextStep => s.stepType === EStepType.NEXT;

export type TChangeGpsrServiceDialogProps = {
	open: boolean;
	setClose: () => void;
};

export const ChangeGpsrServiceDialog: FC<TChangeGpsrServiceDialogProps> = ({ open, setClose }) => {
	const dispatch = useDispatch();
	const gpsrPlans = useSelector(getGpsrPlans);
	const steps = useMemo(() => stepsConfig(gpsrPlans ?? []), [gpsrPlans]);
	const idToIndex = useMemo(() => {
		const map = new Map<string, number>();
		steps.forEach((step, index) => map.set(step.id, index));
		return map;
	}, [steps]);

	useEffect(() => {
		if (!gpsrPlans) {
			dispatch(getGpsrPlansRequest());
		}
	}, [dispatch, gpsrPlans]);

	const getPreviousYesNoIndex = (fromIndex: number) => {
		for (let i = fromIndex - 1; i >= 0; i -= 1) {
			if (steps[i].stepType === EStepType.YES_NO) return i;
		}
		return -1;
	};

	const [currentIndex, setCurrentIndex] = useState<number>(0);
	const [answersByParamName, setAnswersByParamName] = useState<AnswersState>({});
	const hasAnyFilledAnswer = useMemo(() => {
		return Object.values(answersByParamName).some((v) => String(v).trim().length > 0);
	}, [answersByParamName]);
	const errase = () => {
		setAnswersByParamName({});
		setCurrentIndex(0);
	};

	const handleClose = () => {
		errase();
		setClose();
	};

	const handleSubmit = () => {
		dispatch(
			changeGpsrServiceRequest({
				newPlanId: Number(answersByParamName.new_plan_id),
				newNumberOfAdditionalProducts: Number(answersByParamName.new_number_of_additional_products),
				newProducts: answersByParamName.new_products,
				productChanges: answersByParamName.product_changes,
				productsRemove: answersByParamName.products_remove,
			}),
		);
		handleClose();
	};

	const currentStep = steps[currentIndex];

	const goToIndex = (nextIndex: number) => {
		if (nextIndex >= 0 && nextIndex < steps.length) {
			setCurrentIndex(nextIndex);
		}
	};

	const goBack = () => {
		const prevIndex = getPreviousYesNoIndex(currentIndex);
		if (prevIndex >= 0) {
			setCurrentIndex(prevIndex);
		}
	};

	const goNextSequential = () => {
		goToIndex(currentIndex + 1);
	};

	const onYesNoSelect = (answer: 'yes' | 'no') => {
		const yesNo = currentStep as YesNoStep;

		if (answer === 'no') {
			const yesId = yesNo.yes;
			const yesIndex = idToIndex.get(yesId);
			if (typeof yesIndex === 'number') {
				const yesStep = steps[yesIndex];
				if (isNextStep(yesStep)) {
					const params = yesStep.params || [];
					setAnswersByParamName((prev) => {
						const next = { ...prev };
						params.forEach((p) => {
							delete next[p.param];
						});
						return next;
					});
				}
			}
		}

		const nextId = yesNo[answer as 'yes' | 'no'];
		const nextIndex = idToIndex.get(nextId);
		if (typeof nextIndex === 'number') {
			goToIndex(nextIndex);
		} else {
			// fallback: go sequentially if id not found
			goNextSequential();
		}
	};

	const updateAnswer = (paramName: string, value: string) => {
		setAnswersByParamName((prev) => ({ ...prev, [paramName]: value }));
	};

	const renderControls = () => {
		if (currentStep.stepType === EStepType.YES_NO) {
			const canGoBack = getPreviousYesNoIndex(currentIndex) >= 0;
			return (
				<Stack direction="row" spacing={2} sx={{ mt: 2 }}>
					<Button variant="outlined" onClick={handleClose} size="small">
						Cancel
					</Button>
					{canGoBack && (
						<Button variant="outlined" onClick={goBack} size="small">
							Back
						</Button>
					)}
					<Button
						variant="contained"
						color="primary"
						onClick={() => onYesNoSelect('yes')}
						size="small"
					>
						Yes
					</Button>
					<Button
						variant="contained"
						color="primary"
						onClick={() => onYesNoSelect('no')}
						size="small"
					>
						No
					</Button>
				</Stack>
			);
		}

		if (currentStep.stepType === EStepType.SUBMIT) {
			const canGoBack = getPreviousYesNoIndex(currentIndex) >= 0;
			return (
				<Stack direction="row" spacing={2} sx={{ mt: 2 }}>
					<Button variant="outlined" onClick={handleClose} size="small">
						Cancel
					</Button>
					<Button
						variant={hasAnyFilledAnswer ? 'outlined' : 'contained'}
						onClick={goBack}
						size="small"
						disabled={!canGoBack}
					>
						Back
					</Button>
					<Button
						variant="contained"
						color="primary"
						onClick={handleSubmit}
						size="small"
						disabled={!hasAnyFilledAnswer}
					>
						Submit
					</Button>
				</Stack>
			);
		}

		// for NEXT steps: disable Next if any param value is empty
		const isNextDisabled = (() => {
			if (currentStep.stepType === EStepType.NEXT) {
				const params = currentStep.params || [];
				return params.some((p) => {
					const v = answersByParamName[p.param] ?? '';
					return p.paramType === EParamType.TEXT ? v.trim().length === 0 : v === '';
				});
			}
			return false;
		})();

		const canGoBack = getPreviousYesNoIndex(currentIndex) >= 0;
		return (
			<Stack direction="row" spacing={2} sx={{ mt: 2 }}>
				<Button variant="outlined" onClick={handleClose} size="small">
					Cancel
				</Button>
				<Button variant="outlined" onClick={goBack} disabled={!canGoBack} size="small">
					Back
				</Button>
				<Button
					variant="contained"
					color="primary"
					onClick={goNextSequential}
					size="small"
					disabled={isNextDisabled}
				>
					Next
				</Button>
			</Stack>
		);
	};

	const renderContent = () => {
		switch (currentStep.stepType) {
			case EStepType.YES_NO: {
				return (
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							height: '200px',
							justifyContent: 'center',
						}}
					>
						<Typography
							variant="subtitle1"
							sx={{
								whiteSpace: 'pre-line',
								fontWeight: 'bold',
								fontSize: '1.3rem',
								color: 'primary.main',
							}}
						>
							{currentStep.question}
						</Typography>
					</Box>
				);
			}
			case EStepType.NEXT: {
				const params = currentStep.params || [];
				return (
					<Box>
						<Typography
							variant="subtitle1"
							sx={{
								whiteSpace: 'pre-line',
								// fontWeight: 'bold',
								fontSize: '1rem',
								// color: 'primary.main',
								mb: 2,
							}}
						>
							{currentStep.question}
						</Typography>
						<Stack spacing={2}>
							{params.map((p) => {
								const { param, name } = p;
								const value = answersByParamName[param] ?? '';
								if (p.paramType === EParamType.TEXT) {
									return (
										<Box key={param}>
											<Typography variant="body2" sx={{ mb: 0.5 }}>
												{name}
											</Typography>
											<TextField
												fullWidth
												multiline
												minRows={3}
												value={value}
												onChange={(e) => updateAnswer(param, e.target.value)}
												placeholder="Enter details here"
											/>
										</Box>
									);
								}
								if (p.paramType === EParamType.NUMBER) {
									return (
										<Box key={param}>
											<Typography variant="body2" sx={{ mb: 0.5 }}>
												{name}
											</Typography>
											<TextField
												fullWidth
												type="number"
												value={value}
												onChange={(e) => updateAnswer(param, e.target.value)}
												placeholder="Enter number"
											/>
										</Box>
									);
								}
								// SELECT
								return (
									<Box key={param}>
										<Typography variant="body2" sx={{ mb: 0.5 }}>
											{name}
										</Typography>
										<Select
											fullWidth
											value={value}
											displayEmpty
											onChange={(e) => updateAnswer(param, String(e.target.value))}
										>
											<MenuItem value="" disabled>
												Select an option
											</MenuItem>
											{p.options.map((opt) => (
												<MenuItem key={opt.id} value={opt.id}>
													{opt.name}
												</MenuItem>
											))}
										</Select>
									</Box>
								);
							})}
						</Stack>
					</Box>
				);
			}
			case EStepType.SUBMIT: {
				// Render a simple review list of collected answers
				return (
					<Box>
						{hasAnyFilledAnswer && (
							<Typography
								variant="subtitle1"
								sx={{
									whiteSpace: 'pre-line',
									fontSize: '1rem',
									mb: 2,
								}}
							>
								{currentStep.question}
							</Typography>
						)}
						{!hasAnyFilledAnswer && (
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
									justifyContent: 'center',
									height: '200px',
								}}
							>
								<Typography
									variant="h6"
									color="error"
									sx={{ mb: 2, textAlign: 'center', fontWeight: 'bold', fontSize: '1.2rem' }}
								>
									No one field is filled
								</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
									Please go back and fill the fields.
								</Typography>
							</Box>
						)}

						<Stack spacing={1}>
							{hasAnyFilledAnswer &&
								steps
									.filter(isNextStep)
									.map((s) => s.params)
									.flat()
									.map((p) => {
										const { param, name } = p;
										let displayValue: string = answersByParamName[param] ?? '';
										if (p.paramType === EParamType.SELECT) {
											const selected = p.options.find((o) => o.id === Number(displayValue));
											displayValue = selected ? selected.name : displayValue;
										}
										return (
											<Box key={param}>
												<Typography variant="body2" color="text.secondary">
													{name}
												</Typography>
												<Typography variant="body1">{displayValue || '—'}</Typography>
											</Box>
										);
									})}
						</Stack>
					</Box>
				);
			}
			default:
				return null;
		}
	};

	const renderHeaderContentYesNo = () => {
		return (
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					height: '200px',
					justifyContent: 'center',
				}}
			>
				<Typography
					variant="subtitle1"
					sx={{
						whiteSpace: 'pre-line',
						fontWeight: 'bold',
						fontSize: '1.3rem',
						color: 'primary.main',
					}}
				>
					{currentStep.question}
				</Typography>
			</Box>
		);
	};

	const renderHeaderContentNext = () => {
		return (
			<Box>
				<Typography
					variant="subtitle1"
					sx={{
						whiteSpace: 'pre-line',
					}}
				>
					{currentStep.question}
				</Typography>
			</Box>
		);
	};

	return (
		<Dialog
			open={open}
			sx={{
				'& .MuiDialog-paper': {
					width: '800px!important',
					maxWidth: '800px!important',
					minHeight: '400px!important',
					borderRadius: '12px',
				},
			}}
		>
			<DialogTitle color="primary" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
				Change GPSR Service
			</DialogTitle>
			<DialogContent>
				<Box>{renderContent()}</Box>
			</DialogContent>
			<DialogActions>{renderControls()}</DialogActions>
		</Dialog>
	);
};
